import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

// Create the Cart Context
const CartContext = createContext();

// Custom hook to use the Cart Context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const GUEST_CART_KEY = 'bds_guest_cart';
const GUEST_WISHLIST_KEY = 'bds_guest_wishlist';

const readLS = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || []; }
  catch { return []; }
};

// Cart Provider Component
export const CartProvider = ({ children }) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [guestCart, setGuestCart] = useState(() => readLS(GUEST_CART_KEY));
  const [guestWishlist, setGuestWishlist] = useState(() => readLS(GUEST_WISHLIST_KEY));

  // Persist guest data to localStorage
  useEffect(() => {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(guestCart));
  }, [guestCart]);

  useEffect(() => {
    localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(guestWishlist));
  }, [guestWishlist]);

  const refreshCounts = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // ── Guest Cart ────────────────────────────────────────────────────────────

  const addToGuestCart = useCallback((item) => {
    setGuestCart(prev => {
      const existing = prev.find(c => c.product_id === item.product_id);
      if (existing) {
        return prev.map(c =>
          c.product_id === item.product_id ? { ...c, qty: c.qty + item.qty } : c
        );
      }
      return [...prev, item];
    });
  }, []);

  const removeFromGuestCart = useCallback((productId) => {
    setGuestCart(prev => prev.filter(c => c.product_id !== productId));
  }, []);

  const updateGuestCartQty = useCallback((productId, qty) => {
    if (qty < 1) {
      setGuestCart(prev => prev.filter(c => c.product_id !== productId));
    } else {
      setGuestCart(prev =>
        prev.map(c => c.product_id === productId ? { ...c, qty } : c)
      );
    }
  }, []);

  // ── Guest Wishlist ────────────────────────────────────────────────────────

  const toggleGuestWishlist = useCallback((item) => {
    setGuestWishlist(prev => {
      const isIn = prev.some(w => w.product_id === item.product_id);
      return isIn
        ? prev.filter(w => w.product_id !== item.product_id)
        : [...prev, item];
    });
  }, []);

  const isInGuestWishlist = useCallback((productId) => {
    return readLS(GUEST_WISHLIST_KEY).some(w => w.product_id === productId);
  }, []);

  // ── Sync to account after login ───────────────────────────────────────────

  const syncGuestToAccount = useCallback(async (account) => {
    const cart = readLS(GUEST_CART_KEY);
    const wishlist = readLS(GUEST_WISHLIST_KEY);
    if (!cart.length && !wishlist.length) return;

    const base = process.env.REACT_APP_API_URL;
    const apiKey = process.env.REACT_APP_API_KEY;
    const headers = { 'Content-Type': 'application/json', api: apiKey, auth: account.token };

    const post = (endpoint, data) =>
      axios.post(`${base}${endpoint}`, data, { headers }).catch(() => null);

    await Promise.allSettled([
      ...cart.map(item =>
        post('account/cart/add', {
          product_id: item.product_id,
          qty: item.qty,
          ...(item.options && { options: item.options }),
        })
      ),
      ...wishlist.map(item =>
        post('account/wishlist/add', { product_id: item.product_id })
      ),
    ]);

    setGuestCart([]);
    setGuestWishlist([]);
    localStorage.removeItem(GUEST_CART_KEY);
    localStorage.removeItem(GUEST_WISHLIST_KEY);
  }, []);

  const value = {
    refreshTrigger,
    refreshCounts,
    guestCart,
    guestWishlist,
    addToGuestCart,
    removeFromGuestCart,
    updateGuestCartQty,
    toggleGuestWishlist,
    isInGuestWishlist,
    syncGuestToAccount,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
